import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NONE_LICENSE_VALUE, type UserWithLicense } from "../types";

interface UserTableProps {
    canEdit: boolean;
    licenseOptions: {
        label: string;
        value: string;
        available: number;
    }[];
    onToggleAdmin: (userId: string, isAdmin: boolean) => void;
    onToggleLicense: (userId: string, newLicenseId: string) => void;
    users: UserWithLicense[];
}

const UserTable = ({
    canEdit,
    licenseOptions,
    onToggleAdmin,
    onToggleLicense,
    users,
}: UserTableProps) => {
    const { t } = useTranslation();

    return (
        <table className="w-full table-fixed">
            <thead>
                <tr className="border-b">
                    <th className="w-2/5 max-w-0 py-2 pr-4 text-left text-sm font-semibold">
                        {t("settings.userManagement.email")}
                    </th>
                    <th className="py-2 pr-4 text-left text-sm font-semibold">
                        {t("settings.userManagement.licenseType")}
                    </th>
                    <th className="w-24 py-2 pl-2 text-center text-sm font-semibold">
                        {t("settings.userManagement.isAdmin")}
                    </th>
                </tr>
            </thead>
            <tbody>
                {users.map((user) => {
                    const isProtectedLastAdmin =
                        user.isAdmin && user.isLastAdmin;
                    const canModifyUser = canEdit && !isProtectedLastAdmin;

                    return (
                        <tr key={user.userId}>
                            <td className="max-w-0 py-2 pr-4 text-sm">
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="min-w-0 flex-1 truncate">
                                        {user.userMail}
                                    </span>
                                    {user.isMonthlyAnalyzedMailLimitReached && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <AlertTriangle className="h-4 w-4 shrink-0 text-warning-base" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {t(
                                                        "settings.userManagement.mailLimitReached",
                                                    )}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </td>
                            <td className="py-2 pr-4">
                                <label
                                    className="sr-only"
                                    htmlFor={`license-select-${user.userId}`}
                                >
                                    {t("settings.userManagement.licenseType")}{" "}
                                    {t("settings.userManagement.selectFor")}{" "}
                                    {user.userMail}
                                </label>
                                <Select
                                    disabled={!canEdit}
                                    onValueChange={(value) =>
                                        onToggleLicense(user.userId, value)
                                    }
                                    value={user.licenseId ?? NONE_LICENSE_VALUE}
                                >
                                    <SelectTrigger
                                        id={`license-select-${user.userId}`}
                                    >
                                        <SelectValue
                                            placeholder={t(
                                                "settings.userManagement.selectRole",
                                            )}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {canModifyUser && (
                                            <SelectItem
                                                value={NONE_LICENSE_VALUE}
                                            >
                                                {t(
                                                    "settings.userManagement.noLicense",
                                                )}
                                            </SelectItem>
                                        )}
                                        {licenseOptions.map((option) => {
                                            const isCurrentLicense =
                                                user.licenseId === option.value;
                                            const isUnavailable =
                                                option.available <= 0 &&
                                                !isCurrentLicense;

                                            return (
                                                <SelectItem
                                                    key={option.value}
                                                    disabled={isUnavailable}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </td>
                            <td className="py-2 pl-2">
                                <div className="flex justify-center">
                                    <Switch
                                        checked={user.isAdmin}
                                        disabled={!canModifyUser}
                                        onCheckedChange={(checked) =>
                                            onToggleAdmin(user.userId, checked)
                                        }
                                    />
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export default UserTable;
